/**
 * Billing Summary — permanent configuration.
 *
 * These are real vocabularies, not stand-in data: the filter rosters carried over
 * from the legacy report and the billable line types offered when logging
 * development hours. The listing itself is API-backed
 * (`GET /api/billing-summary`) — see `billingSummary.mappers.ts`.
 */
/** No filter applied — every dropdown shows its placeholder. */
export const EMPTY_FILTERS = Object.freeze({
    source: "",
    excluded_client: "",
    sync_status: "",
    invoice_status: "",
    diff_amount: "",
    am_id: "",
    type: "",
});
/**
 * The 7 filter dropdowns, left to right as in the reference dashboard.
 *
 * Each carries a short `label` and a `leftIcon` so it renders as a Commission
 * Tracker-style pill ("AM: All" with a leading glyph). `am_id` and `type` reuse
 * that module's exact icons, since they're the same two filters.
 *
 * Option order is authored deliberately (workflow order for the statuses, A→Z for
 * the rosters), so the dropdowns render with `sortByLabel={false}`.
 *
 * Every key here is a query param on `GET /api/billing-summary` — all filtering is
 * server-side, so the page renders the response rows as they arrive. `sync_status` is
 * the one exception: it's sent and accepted, but inert until something records a
 * per-client sync state.
 */
export const BILLING_SUMMARY_FILTERS = [
    {
        key: "source",
        label: "Source",
        leftIcon: "/assets/meas-channel.svg",
        placeholder: "Source: All",
        // Value = the measurement-channel name exactly as stored on
        // `client_commission_goals`, since that's what the API matches against. The
        // full stored vocabulary less "None", which is dropped server-side.
        options: [
            { value: "AWIN", label: "AWIN" },
            { value: "AvantLink", label: "AvantLink" },
            { value: "Bing Ads", label: "Bing Ads" },
            { value: "Commission Junction", label: "Commission Junction" },
            { value: "Facebook Ads", label: "Facebook Ads" },
            { value: "Google Ads", label: "Google Ads" },
            { value: "Impact", label: "Impact" },
            { value: "Levanta", label: "Levanta" },
            { value: "Mgmt Fee Only", label: "Mgmt Fee Only" },
            { value: "Pepperjam", label: "Pepperjam" },
            { value: "Rakuten", label: "Rakuten" },
            { value: "Shopify", label: "Shopify" },
            { value: "Tab", label: "Tab" },
        ],
        searchable: true,
    },
    {
        key: "excluded_client",
        label: "Excluded",
        leftIcon: "/assets/visibleClients.svg",
        placeholder: "Excluded: All",
        options: [
            { value: "no_billing", label: "No Billing" },
            { value: "no_client_info", label: "No Client Info" },
            { value: "no_network_connected", label: "No Network Connected" },
        ],
    },
    {
        key: "sync_status",
        label: "Sync",
        leftIcon: "/assets/refresh.svg",
        placeholder: "Sync: All",
        options: [
            { value: "success", label: "Synced" },
            { value: "pending", label: "Not Synced" },
            { value: "error", label: "Error" },
        ],
    },
    {
        key: "invoice_status",
        label: "Invoice",
        leftIcon: "/assets/bill.svg",
        placeholder: "Invoice: All",
        options: [
            { value: "synced", label: "Not Sent" },
            { value: "sent", label: "Sent" },
            { value: "viewed", label: "Viewed" },
            { value: "delivery_issue", label: "Delivery Issue" },
            { value: "partially_paid", label: "Partially Paid" },
            { value: "paid", label: "Paid" },
        ],
    },
    {
        key: "diff_amount",
        label: "Diff",
        leftIcon: "/assets/money-bag.svg",
        placeholder: "Diff: All",
        options: [
            { value: "match", label: "Invoice Match" },
            { value: "miss", label: "Invoice Mismatch" },
        ],
    },
    {
        key: "am_id",
        label: "AM",
        leftIcon: "/assets/userC.svg",
        placeholder: "AM: All",
        // Populated from the designation-wise users API — the same roster Commission
        // Tracker and Account Performance use, so the ids match what `am_id` expects.
        options: [],
        searchable: true,
    },
    {
        key: "type",
        label: "Type",
        leftIcon: "/assets/TypeC.svg",
        placeholder: "Type: All",
        options: [
            { value: "", label: "All" },
            { value: "paid_media", label: "Paid Media" },
            { value: "affiliate", label: "Affiliate" },
            { value: "both", label: "Both" },
        ],
    },
];
/**
 * The `diff_amount` value for "Invoice Mismatch" — rows whose invoice doesn't match
 * their Total Bill. Named because the page keys the "Update Total Bill" action off it:
 * overriding a bill by hand only makes sense while looking at the mismatched rows.
 */
export const INVOICE_MISMATCH_FILTER = "miss";
/**
 * Internal approval states offered in the Invoice Preview modal, stored on the invoice's
 * `approval_status`. The values are lowercase to match what the legacy report wrote, so
 * rows approved before this port still read back correctly.
 */
export const INVOICE_APPROVAL_STATUSES = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
];
/** Billable line types offered in the "Add Development Hour(s)" repeater. */
export const DEVELOPMENT_HOUR_TYPES = [
    { value: "website_development_optimizations", label: "Website Development Optimizations" },
    { value: "publisher_placement_fee", label: "Publisher Placement Fee" },
];
